"use client";

import React from "react";
import { ArrowRight, EyeOff, Lock, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface PrivacyPrinciple {
  id: number;
  title: string;
  description: string;
  iconName: "ShieldCheck" | "EyeOff" | "Lock";
}
const principles: PrivacyPrinciple[] = [{
  id: 1,
  title: "Zero data monetization",
  description: "We never sell, rent, or trade reader contact information, research downloads, or browsing patterns to third-party data brokers or advertisers.",
  iconName: "EyeOff"
}, {
  id: 2,
  title: "Global compliance standards",
  description: "Our infrastructure adheres strictly to GDPR, CCPA, and international data governance protocols with enterprise-grade encryption.",
  iconName: "ShieldCheck"
}, {
  id: 3,
  title: "Unconditional reader control",
  description: "You retain complete ownership over your data. Manage your communication preferences, export your download history, or delete your record in one click.",
  iconName: "Lock"
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 14
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
export default function Commitment() {
  return <section id="responsible-data-use-privacy-commitment" data-nav="light" className="border-b border-border bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-[1280px] px-5 sm:px-8 lg:px-12">
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="mx-auto max-w-[840px] text-center">
          <motion.div variants={itemVariants} className="flex justify-center">
            <Badge variant="outline" className="border-border bg-card px-3 py-1 text-xs font-medium tracking-wider text-muted-foreground uppercase">
              Institutional Integrity
            </Badge>
          </motion.div>

          {/* Double Masthead Broadside Rule */}
          <motion.div variants={itemVariants} className="my-6 mx-auto flex w-24 flex-col items-center gap-1" aria-hidden="true">
            <div className="h-[1px] w-full bg-foreground/70" />
            <div className="h-[0.5px] w-2/3 bg-foreground/40" />
          </motion.div>

          <motion.h2 variants={itemVariants} className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
            Responsible data use and privacy commitment
          </motion.h2>

          <motion.p variants={itemVariants} className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            MinfoMedia operates as an independent research publisher. We hold your trust to the highest standard by processing all subscriber and readership data transparently, securely, and strictly within global compliance frameworks.
          </motion.p>
        </motion.div>

        {/* 3-Column Structured Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {principles.map((item, index) => {
          return <motion.div key={item.id} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full border border-border bg-card p-6 shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-border/80 hover:shadow-md">
                  <CardContent className="flex flex-col gap-4 p-0">
                    <div className="flex size-10 items-center justify-center rounded-sm bg-muted text-foreground">
                      {item.iconName === "EyeOff" && <EyeOff className="size-5" />}
                      {item.iconName === "ShieldCheck" && <ShieldCheck className="size-5" />}
                      {item.iconName === "Lock" && <Lock className="size-5" />}
                    </div>
                    <div>
                      <h3 className="font-serif text-lg font-semibold text-foreground">
                        {item.title}
                      </h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {item.description}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Action strip */}
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="mt-12 flex flex-col items-center justify-center gap-4 text-center sm:flex-row">
          <span className="text-sm text-muted-foreground">
            Questions regarding our data handling or research standards?
          </span>
          <Button asChild variant="outline" className="rounded-sm font-medium">
            <Link to="/PrivacyDataProtectionPage" className="inline-flex items-center gap-2">
              <span>Read our privacy policy</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}